import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { Z_MODAL_CHROME } from './zLayers'
import { HotkeyBadge } from './HotkeyBadge'

// Re-exported for back-compat: HotkeyBadge used to live here. It now has its own
// module so the DrawingBar flyout can share the exact same badge.
export { HotkeyBadge }

/* ------------------------------------------------------------------ */
/*  Shared context-menu chrome                                         */
/* ------------------------------------------------------------------ */
/**
 * Row + glyph + submenu primitives shared by the chart's right-click menus
 * (`PriceScaleContextMenu`, `ChartBodyContextMenu`). One source of truth so the
 * two menus never drift in row metrics, hover, or glyph alignment. Chrome family
 * matches SettingsModal / IndicatorSettingsDrawer.
 */

export type Glyph = 'check' | 'radio' | null

/**
 * One clickable menu row. A fixed-width leading column keeps labels aligned
 * whether the row shows a checkmark, a radio dot, an icon, or nothing.
 * Selecting runs the handler; the parent decides whether to close (each handler
 * is a pure emit).
 *
 * `icon` shares that column with the state glyphs and yields to them: a row that
 * is showing check/radio is communicating STATE, and an icon in the same slot
 * would either collide or push the label out of alignment with its neighbours.
 *
 * A row that needs BOTH — an identifying icon AND a selected state, as the chart
 * type rows do — passes `icon` plus `trailingGlyph`, putting the state on the
 * right. That matches the toolbar's own chart-type dropdown, so the two surfaces
 * read the same way. `aria-checked` is set whenever `trailingGlyph` is supplied,
 * so selection is assertable without inspecting the artwork.
 */
export function MenuRow({
  glyph,
  icon,
  trailingGlyph,
  hotkey,
  onSelect,
  children,
}: {
  glyph: Glyph
  icon?: ReactNode
  trailingGlyph?: Glyph
  /** Keyboard-shortcut label, right-aligned after the label as a `<kbd>` badge. */
  hotkey?: string
  onSelect: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      role="menuitem"
      aria-checked={trailingGlyph === undefined ? undefined : trailingGlyph !== null}
      onClick={onSelect}
      className="flex items-center gap-2 px-2.5 py-1.5 text-left text-xs text-[hsl(var(--chart-text))] transition-colors hover:bg-accent"
    >
      <span
        className={
          'flex h-3.5 w-3.5 shrink-0 items-center justify-center ' +
          (glyph === null && icon ? 'text-[hsl(var(--chart-text-muted))]' : 'text-primary')
        }
      >
        {glyph === 'check' && <CheckGlyph />}
        {glyph === 'radio' && <RadioGlyph />}
        {glyph === null && icon}
      </span>
      <span className="whitespace-nowrap">{children}</span>
      {hotkey && <HotkeyBadge hotkey={hotkey} className="ml-auto pl-3" />}
      {trailingGlyph && (
        <span
          className={
            'flex h-3.5 w-3.5 shrink-0 items-center justify-center pl-2 text-primary ' +
            (hotkey ? '' : 'ml-auto')
          }
        >
          {trailingGlyph === 'check' ? <CheckGlyph /> : <RadioGlyph />}
        </span>
      )}
    </button>
  )
}

/**
 * A submenu row: a label + right chevron that reveals a flyout of child rows on
 * hover. A short close delay on mouseleave lets the cursor travel from the parent
 * row into the flyout without it snapping shut.
 */
export function Submenu({
  label,
  icon,
  children,
}: {
  label: string
  icon?: ReactNode
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const wrapRef = useRef<HTMLDivElement>(null)
  const flyoutRef = useRef<HTMLDivElement>(null)
  // Which side the flyout opens on + a vertical nudge, resolved once the flyout
  // is measured. Default is the right edge (the common case); flip LEFT when a
  // right-opening flyout would run off the viewport's right edge, and lift it up
  // when it would run off the bottom — so a submenu opened near a screen corner
  // stays fully on-screen and clickable instead of clipping its rows.
  const [pos, setPos] = useState<{ side: 'left' | 'right'; top: number } | null>(null)

  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return
    }
    const wrap = wrapRef.current
    const fly = flyoutRef.current
    if (!wrap || !fly) return
    const margin = 8
    const rect = wrap.getBoundingClientRect()
    const w = fly.offsetWidth
    const h = fly.offsetHeight
    // The -ml-1 overlap means the right-opening flyout starts 4px left of the
    // row's right edge; account for it so the flip triggers at the true overflow.
    const side: 'left' | 'right' =
      rect.right - 4 + w + margin > window.innerWidth ? 'left' : 'right'
    const overflowBottom = rect.top + h + margin - window.innerHeight
    setPos({ side, top: overflowBottom > 0 ? -overflowBottom : 0 })
  }, [open])

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={() => {
        clearTimeout(closeTimer.current)
        setOpen(true)
      }}
      onMouseLeave={() => {
        closeTimer.current = setTimeout(() => setOpen(false), 120)
      }}
    >
      <button
        type="button"
        role="menuitem"
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs text-[hsl(var(--chart-text))] transition-colors hover:bg-accent"
      >
        <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center text-[hsl(var(--chart-text-muted))]">
          {icon}
        </span>
        <span className="flex-1 whitespace-nowrap">{label}</span>
        <ChevronRight />
      </button>
      {open && (
        <div
          ref={flyoutRef}
          role="menu"
          // Opens to the right by default, overlapping the parent's edge by a hair
          // (-ml-1 / -mr-1) so there's no dead gap for the cursor to fall through.
          // Flips to the LEFT (right-full) near the viewport's right edge, and is
          // lifted up (`top`) when it would overflow the bottom. Hidden until the
          // measure pass resolves `pos`, so it never flashes in the wrong place.
          style={{
            zIndex: Z_MODAL_CHROME,
            top: pos?.top ?? 0,
            visibility: pos ? 'visible' : 'hidden',
          }}
          className={
            'absolute flex min-w-[180px] flex-col rounded-lg border border-primary/15 bg-popover py-1 shadow-2xl shadow-black/50 ' +
            (pos?.side === 'left' ? 'right-full -mr-1' : 'left-full -ml-1')
          }
        >
          {children}
        </div>
      )}
    </div>
  )
}

export function Divider() {
  return <div className="my-1 h-px bg-border" role="separator" />
}

/* ------------------------------------------------------------------ */
/*  Glyphs                                                             */
/* ------------------------------------------------------------------ */

export function CheckGlyph() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

export function RadioGlyph() {
  return (
    <svg width={8} height={8} viewBox="0 0 8 8" aria-hidden="true">
      <circle cx="4" cy="4" r="4" fill="currentColor" />
    </svg>
  )
}

function ChevronRight() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0 text-[hsl(var(--chart-text-muted))]"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}
